'use client';
import { useTheme } from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useSpecialtyCalculators } from '@/lib/hooks/useSpecialtyCalculators';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  OutputField,
  OutputGraphic,
  Styled2ColGrid,
  StyledColumn,
  StyledTextSlider,
} from './components';

type BorrowingPowerFormValues = {
  grossMonthlyIncome: number;
  monthlyExpenses: number;
  existingDebtRepayments: number;
  annualRate: number;
  loanTermYears: number;
  dscr: number;
};

const BorrowingPower = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();
  const { calculateBorrowingPower } = useSpecialtyCalculators();

  const methods = useForm<BorrowingPowerFormValues>({
    defaultValues: {
      grossMonthlyIncome: 10000,
      monthlyExpenses: 3000,
      existingDebtRepayments: 0,
      annualRate: 6.0,
      loanTermYears: 30,
      dscr: 1.2,
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const result = calculateBorrowingPower({
    grossMonthlyIncome: values.grossMonthlyIncome || 0,
    monthlyExpenses: values.monthlyExpenses || 0,
    existingDebtRepayments: values.existingDebtRepayments || 0,
    annualRate: values.annualRate || 0,
    loanTermYears: values.loanTermYears || 0,
    dscr: values.dscr || 1,
  });

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="BorrowingPower"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Enter your financials'}>
          <Controller
            name="grossMonthlyIncome"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Gross Monthly Income"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="monthlyExpenses"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Monthly Living Expenses"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="existingDebtRepayments"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Existing Debt Repayments"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="annualRate"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Annual Interest Rate"
                suffix="%"
                value={field.value}
                defaultMax={20}
                step={0.1}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="loanTermYears"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Loan Term"
                suffix={field.value === 1 ? ' year' : ' years'}
                value={field.value}
                minValue={1}
                maxValue={30}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="dscr"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Debt Service Coverage Ratio"
                value={field.value}
                step={0.1}
                minValue={1.0}
                maxValue={3.0}
                defaultMax={3}
                helperText="*Typical range: 1.0–2.0"
                onChange={field.onChange}
              />
            )}
          />
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Borrowing Summary'}>
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Monthly Expenses',
                  value: values.monthlyExpenses || 0,
                  color: theme.palette.brand.secondary,
                },
                {
                  label: 'New Repayment',
                  value: result.maxMonthlyRepaymentRaw,
                  color: theme.palette.brand.primary,
                },
              ]}
            />
            <StyledColumn>
              <OutputField label="Net Monthly Income" value={`$${result.netMonthlyIncome}`} />
              <OutputField label="Max Monthly Repayment" value={`$${result.maxMonthlyRepayment}`} />
            </StyledColumn>
          </Styled2ColGrid>
          <OutputField label="Estimated Borrowing Power" value={`$${result.maxLoan}`} highlight />
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default BorrowingPower;
